import { Store, Roles } from "_enums/enums";
import Moment from "moment";
import { ReplaySubject, Subject } from "rxjs";
import DOMPurify from "dompurify";

/** localStorage only stores strings; never persist the literal "undefined" / "null". */
export function normalizeLandlordLogoImage(value) {
  if (value == null) return "";
  const s = typeof value === "string" ? value.trim() : String(value);
  if (!s || s === "undefined" || s === "null") return "";
  return s;
}

export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html);
}

/**
 * Removes inline images from Quill comment HTML.
 *
 * The comment editors expose no image button, so an image only gets in by paste
 * or drag-drop, which Quill embeds inline as a `data:` URI. A pasted screenshot
 * becomes hundreds of KB of base64 on one line. That is the payload that took the
 * API down on 21 Sep 2026: the server lays every comment out with pdfjs when a
 * ticket is exported to PDF, and pdfjs is super-quadratic in the length of a
 * single unbroken token, so one pasted image never finished rendering and pinned
 * the CPU. See fixify-server/utils/sanitizeRichText.js for the matching backend
 * guard — this is the first line of defence, applied before the value is ever
 * sent, so an image cannot be stored in the first place.
 *
 * Every <img> is dropped, not just data: ones: these editors never legitimately
 * contain an image, so anything here is a paste accident.
 */
export function stripInlineImages(html) {
  if (typeof html !== "string" || !html) return html;
  return html
    .replace(/<img\b[^>]*>/gi, "")
    .replace(/\bdata:[a-zA-Z0-9.+-]+\/[a-zA-Z0-9.+-]+(?:;[a-zA-Z0-9.+=-]+)*,[A-Za-z0-9+/=%:_.~-]*/gi, "");
}

/**
 * Trims the trailing empty paragraphs ReactQuill leaves behind.
 *
 * Quill re-serialises its value on mount — an empty editor becomes "<p><br></p>" —
 * so a raw `value !== initialValue` check reports a change on a form the user
 * never touched. Compare normalised values, and persist the normalised string.
 */
export function removeTrailingEmptyLines(html) {
  if (!html) return "";

  return html
    .replace(/\s+$/g, "")
    .replace(/(<p><br><\/p>)+$/g, "")
    .trim();
}

/** Plain-text length of Quill HTML — markup must not eat the user's character budget. */
export function htmlTextLength(html) {
  if (!html) return 0;
  const text = new DOMParser().parseFromString(html, "text/html").body.textContent || "";
  return text.trim().length;
}

export function setUserDataInLocalStorage(userData) {
  // A mid-signup Landlord/TradesPerson (email verified, address step still pending) carries
  // a scoped profileCompletionToken instead of a real jwtToken — that must never flip
  // isLoggedIn or land in the general session token slot. See AuthProtectedRoutes.js /
  // AuthPublicRouteHOC.js, which gate on isLoggedIn, and redux/user/userActions.js updateUser().
  if (userData.jwtToken) {
    localStorage.setItem(Store.token, userData.jwtToken);
    localStorage.setItem(Store.isLoggedIn, true);
  }
  if (userData.profileCompletionToken) {
    localStorage.setItem(Store.profileCompletionToken, userData.profileCompletionToken);
  }
  localStorage.setItem(Store.role, userData.role);
  const displayRole = userData.role === Roles.Landlord ? userData.displayRole || "Landlord" : userData.displayRole || userData.role;
  localStorage.setItem(Store.displayRole, displayRole);
  localStorage.setItem(Store.email, userData.email);
  localStorage.setItem(Store.isProfileComplete, userData.isProfileComplete);
  localStorage.setItem(Store.trade, userData.trade || "");
  localStorage.setItem(Store.userId, userData._id);
  localStorage.setItem(Store.businessName, userData.businessName);
  localStorage.setItem(Store.createdAt, userData.createdAt);
  localStorage.setItem(Store.updatedAt, userData.updatedAt);
  localStorage.setItem(Store.contact, userData.contact);
  localStorage.setItem(Store.profileImage, userData.profileImage);
  localStorage.setItem(Store.firstName, userData.firstName);
  localStorage.setItem(Store.lastName, userData.lastName);
  localStorage.setItem(Store.landlordBusinessName, userData.landlordBusinessName || "");
  localStorage.setItem(Store.landlordLogoImage, normalizeLandlordLogoImage(userData.landlordLogoImage));
  localStorage.setItem(Store.landlordDisplayRole, userData.landlordDisplayRole || "");
}

export function GetValueByKeys(keys, arrayOfObj) {
  let values = [];
  arrayOfObj.forEach((obj) => {
    for (let objKey of Object.keys(obj)) {
      if (keys.includes(objKey)) {
        values.push(obj[objKey]);
      }
    }
  });
  return values;
}

//datepipe
export function DatePipe(date, format = "do, MMM YYYY, h:mm A") {
  Moment.locale("en");
  return Moment(date).format(format);
}
//timepipe
export function OnlyTimePipe(date, format = "h:mm A") {
  Moment.locale("en");
  return Moment(date).format(format);
}
//only date pipe format("dddd, MMMM Do YYYY, h:mm:ss a"); // "Sunday, February 14th 2010, 3:25:50 pm"
export function OnlyDatePipe(date, format = "ddd, DD MMM YYYY") {
  Moment.locale("en");
  return Moment(date).format(format);
}
export function CustomDateFormat(date, format = "ddd, DD MMM YYYY, h:mm") {
  Moment.locale("en");
  return Moment(date).format(format);
}
export function Time24Hour(date) {
  return OnlyDatePipe(date) + ", " + new Date(date).toTimeString().split(" ")[0].split(":").splice(0, 2).join(":");
}
//pagetitle replay subject
export let PageTitle$ = new ReplaySubject(1);
export function SetPageTitle(title = "testing") {
  PageTitle$.next(title);
}

//annoument add status
export let IsAnnouncementAdded$ = new ReplaySubject(1);
export function SetAnnouncementAddStatus(status) {
  IsAnnouncementAdded$.next(status);
}

//hide hover sidebar on route change
export let IsSidebarLinkClicked$ = new ReplaySubject(1);
export function HideHoverSidebar(status) {
  IsSidebarLinkClicked$.next(status);
}

//notification data
export let Notification$ = new ReplaySubject(1);
export function SendNotificationData(notification) {
  Notification$.next(notification);
}

//loading spinner data
export let LoadingSpinnerStatus$ = new ReplaySubject(1);
export function SetLoadingStatus() {
  /**
   * LoadingSpinnerStatus$.next(status);
   *  */
}

//full screen modal
export let FullScreenModalData$ = new Subject(null);
export function SetFullScreenModalComponent(c) {
  FullScreenModalData$.next(c);
}

export function TimeToDate(time) {
  let t = time.split(":");
  let d = new Date();
  d.setHours(t[0]);
  d.setMinutes(t[1]);
  d.setSeconds(0);
  return d;
}
